var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
/* eslint-disable camelcase */
import { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Editor } from '@tinymce/tinymce-react';
import Loader from 'components/loader/loader';
import useTinySettings from 'hooks/use-tiny-settings';
import { Microservices } from 'services/microservices/microservices';
import showNotification from 'notifications/notifications';
import useAuthentication from 'hooks/use-authentication';
import { getAccountIdFromStorage } from 'helpers/customBranding';
const RichTextEditorComponent = ({ value, identifier, onChange }) => {
    const microservices = new Microservices();
    const { getUserIdFromToken } = useAuthentication();
    const { t, i18n } = useTranslation();
    const [content, setContent] = useState(value);
    const [loading, setLoading] = useState(true);
    const [uploadingFile, setUploadingFile] = useState(false);
    const { getWirisSetting, getWirisPlugin } = useTinySettings();
    const wirisPlugin = getWirisPlugin();
    const wirisPluginUrl = getWirisSetting('pluginUrl');
    useEffect(() => {
        if (value !== content) {
            setContent(value);
        }
    }, [value]);
    return (_jsxs(_Fragment, { children: [loading && _jsx(Loader, {}, void 0), uploadingFile && (ReactDOM.createPortal(_jsx(Loader, { isFullWith: true }, void 0), 
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            document.getElementById('modal'))), _jsxs("div", Object.assign({ className: loading ? 'd-none' : '' }, { children: [_jsx(Editor, { tinymceScriptSrc: process.env.REACT_APP_SUMADI_TINY_MCE_URL, initialValue: content, onEditorChange: onChange, onInit: () => setLoading(false), init: {
                            language: i18n.language,
                            width: '100%',
                            height: '250px',
                            plugins: [
                                'autoresize',
                                'preview',
                                'searchreplace',
                                'autosave',
                                'visualblocks',
                                'visualchars',
                                'fullscreen',
                                'image',
                                'media',
                                'codesample',
                                'table',
                                'charmap',
                                'insertdatetime',
                                'advlist',
                                'lists',
                                'wordcount',
                                'quickbars',
                                'help',
                            ],
                            menu: {
                                file: { title: 'File', items: 'export' },
                                edit: { title: 'Edit', items: 'undo redo | cut copy paste pastetext | selectall | searchreplace' },
                                view: { title: 'View', items: 'visualblocks visualchars | preview fullscreen' },
                                insert: { title: 'Insert', items: 'image media codesample | charmap hr | insertdatetime' },
                                format: { title: 'Format', items: 'bold italic underline strikethrough superscript subscript codeformat | styles blocks fontsize align lineheight | forecolor backcolor | removeformat' },
                                tools: { title: 'Tools', items: 'spellchecker spellcheckerlanguage | wordcount' },
                                table: { title: 'Table', items: 'inserttable | cell row column | advtablesort | tableprops deletetable' },
                                help: { title: 'Help', items: 'help' }
                            },
                            toolbar: 'undo redo | ' +
                                `${(wirisPlugin === null || wirisPlugin === void 0 ? void 0 : wirisPlugin.isEnabled) ? 'tiny_mce_wiris_formulaEditor tiny_mce_wiris_formulaEditorChemistry | ' : ''}` +
                                'bold italic underline strikethrough superscript subscript | ' +
                                'fontsize blocks | ' +
                                'alignleft aligncenter alignright alignjustify | ' +
                                'outdent indent | ' +
                                'numlist bullist ' +
                                'forecolor backcolor casechange' +
                                'removeformat | ' +
                                'charmap | ' +
                                'fullscreen  preview | ' +
                                'image ' +
                                'media codesample | ' +
                                'visualblocks visualchars | ',
                            spellchecker_languages: 'English (United States)=en_US,Portuguese=pt,Spanish=es',
                            quickbars_insert_toolbar: 'quicktable',
                            paste_block_drop: true,
                            paste_data_images: false,
                            smart_paste: false,
                            content_style: 'img { max-width: 100% !important; height: auto; }',
                            external_plugins: {
                                tiny_mce_wiris: wirisPluginUrl || '',
                            },
                            help_tabs: [
                                'shortcuts',
                                'keyboardnav',
                                // 'plugins',
                            ],
                            media_url_resolver: (data, resolve) => {
                                // Only allow uploaded files by the user
                                if (data.url.includes(getAccountIdFromStorage())) {
                                    resolve({ url: data.url });
                                    return;
                                }
                                // Display error alert when previous condition is not met
                                if (data.url !== '') {
                                    showNotification('error', t('RICH_TEXT_EDITOR.MEDIA_NOT_ALLOWED'), 10000);
                                }
                            },
                            file_picker_callback: (cb, value, meta) => {
                                let accept = '*';
                                if (meta.filetype === 'image') {
                                    accept = 'image/*';
                                }
                                if (meta.filetype === 'media') {
                                    accept = '.mov,.mp4,.wmv,.avi,.mp3';
                                }
                                const input = document.createElement('input');
                                input.setAttribute('type', 'file');
                                input.setAttribute('accept', accept);
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                input.onchange = function (ev) {
                                    var _a;
                                    return __awaiter(this, void 0, void 0, function* () {
                                        setUploadingFile(true);
                                        const files = ev.target.files;
                                        const file = files.item(0) || undefined;
                                        if (!file) {
                                            return;
                                        }
                                        const userId = (_a = getUserIdFromToken()) !== null && _a !== void 0 ? _a : '';
                                        const response = yield microservices.uploadFile(file, `${userId}`);
                                        if (response) {
                                            const reader = new FileReader();
                                            const filename = response.url;
                                            reader.onload = () => {
                                                cb(filename);
                                            };
                                            reader.readAsDataURL(file);
                                        }
                                        setUploadingFile(false);
                                        return;
                                    });
                                };
                                input.click();
                            }
                        } }, identifier), _jsx("p", { children: _jsx("em", { children: t('RICH_TEXT_EDITOR.SUPPORTED_MEDIA') }, void 0) }, void 0)] }), void 0)] }, void 0));
};
export default RichTextEditorComponent;
